/**
 * 余额格式化工具
 * 提供统一的余额格式化方法
 */

/**
 * 格式化余额 (保留两位小数)
 * - 自动处理字符串和数字类型
 * - 保留两位小数
 * - 添加货币符号
 * - 带千位分隔符
 *
 * @param balance 余额 (支持 number 或 string 类型)
 * @param currencySymbol 货币符号 (默认 '$')
 * @returns 格式化后的余额字符串
 *
 * @example
 * formatBalance(1234) // '$1,234.00'
 * formatBalance(1234.56) // '$1,234.56'
 * formatBalance(10000) // '$10,000.00'
 * formatBalance(15500) // '$15,500.00'
 * formatBalance('invalid') // '$0.00'
 * formatBalance(null) // '$0.00'
 */
export function formatBalance(balance: number | string | null | undefined, currencySymbol: string = '$'): string {
  // 1. 统一转换为数字
  let numValue = 0

  if (typeof balance === 'number') {
    numValue = balance
  } else if (typeof balance === 'string') {
    numValue = parseFloat(balance)
  }

  // 2. 如果转换失败或为 NaN,返回 0
  if (isNaN(numValue) || !isFinite(numValue)) {
    numValue = 0
  }

  // 3. 使用标准格式 (带千位分隔符和两位小数)
  return `${currencySymbol}${numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * 格式化余额 (不带货币符号,保留两位小数)
 *
 * @param balance 余额 (支持 number 或 string 类型)
 * @returns 格式化后的余额字符串 (不带货币符号)
 *
 * @example
 * formatBalanceWithoutSymbol(1234) // '1,234.00'
 * formatBalanceWithoutSymbol(1234.56) // '1,234.56'
 * formatBalanceWithoutSymbol(10000) // '10,000.00'
 * formatBalanceWithoutSymbol(15500) // '15,500.00'
 */
export function formatBalanceWithoutSymbol(balance: number | string | null | undefined): string {
  return formatBalance(balance, '')
}

/**
 * 格式化限红 (不保留小数)
 * - 自动处理字符串和数字类型
 * - 不保留小数,只显示整数
 * - 不添加货币符号
 * - 带千位分隔符
 *
 * @param amount 金额 (支持 number 或 string 类型)
 * @returns 格式化后的金额字符串
 *
 * @example
 * formatBetLimit(1234) // '1,234'
 * formatBetLimit(1234.56) // '1,235' (四舍五入)
 * formatBetLimit(10000) // '10,000'
 * formatBetLimit(15500) // '15,500'
 * formatBetLimit('invalid') // '0'
 * formatBetLimit(null) // '0'
 */
export function formatBetLimit(amount: number | string | null | undefined): string {
  // 1. 统一转换为数字
  let numValue = 0

  if (typeof amount === 'number') {
    numValue = amount
  } else if (typeof amount === 'string') {
    numValue = parseFloat(amount)
  }

  // 2. 如果转换失败或为 NaN,返回 0
  if (isNaN(numValue) || !isFinite(numValue)) {
    numValue = 0
  }

  // 3. 使用标准格式 (带千位分隔符,不带小数)
  return Math.round(numValue).toLocaleString('en-US')
}

/**
 * 格式化金额 (不保留小数,带千位分隔符)
 * - 用于下注区域显示金额
 * - 不带货币符号
 * - 带千位分隔符
 *
 * @param amount 金额
 * @returns 格式化后的金额字符串
 *
 * @example
 * formatAmount(1234) // '1,234'
 * formatAmount(10000) // '10,000'
 * formatAmount(1234567) // '1,234,567'
 */
export function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US')
}
